import React, { useState, useRef, useEffect, Suspense } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { last, get, nth } from "lodash";
import { ResponsiveImage, SocialShare } from "@quintype/components";

import { SocialShareTemplate } from "../../../arrow";
import { getRelatedStories } from "../../../../api/utils";
import { SvgIconHandler } from "../../atoms/SvgIconHandler";
import { STATIC_KEYWORDS, SUMMARY_STORY_FIELDS } from "../../../constants";
import ExclamatinoLogo from "../../atoms/ExclamationLogo";
import ArrowDashIcon from "../../atoms/Arrow-DashIcon";
import { CategoryIcon } from "../../../arrow/components/Svgs/category-icon";
import { addDatalayerEventStory } from "../../utils";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { useInView } from "react-intersection-observer";
import "./summary-card.m.css";

const ListingAdSlot = React.lazy(() => import("../ListingAdSlot"));

function LazyAdSlot({ index }) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1, // when 10% is in view
  });

  return inView ? (
    <Suspense fallback={<div>Loading ads...</div>}>
      <ListingAdSlot
        adStyleName="ad-slot-size-300x250"
        adType="right_side"
        pageType="summary-page"
        adSlotConfigName="mRec_ad"
        index={index}
      />
    </Suspense>
  ) : (
    <div ref={ref} style={{ height: 100 }}>
      Scroll to load component...
    </div>
  );
}

const SummaryCard = ({ setShowSummary, story, summaryIndex, collectionStories }) => {
  const { FULL_STORY, VIEW_ALL_SUMMARIES, AI_WARNING, POWERED_BY } = STATIC_KEYWORDS;
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentStory, setCurrentStory] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [relatedStories, setRelatedStories] = useState(collectionStories || []);
  const touchStartY = useRef(null);
  const titleRefs = useRef({});
  const [titleHeights, setTitleHeights] = useState({});
  const [allStoriesLength, setAllStoriesLength] = useState(null);

  const { summary_sponsor } = useSelector((state) => get(state, ["qt", "config", "publisher-attributes"], false));
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const pageType = get(qtState, ["pageType"], null);
  const qtConfig = get(qtState, ["config"], null);
  const isStoryPage = pageType === "story-page";
  const isDesktop = useDeviceType() === "desktop";

  const relatedSummaryStoriesUtil = (relStories) => {
    const filteredRelatedStories = relStories.filter((relatedStory) => {
      const lastStoryElements = last(relatedStory.cards)["story-elements"];
      const lastElement = last(lastStoryElements);
      const secondLastElement = nth(lastStoryElements, -2);
      return (
        lastElement.subtype === "summary" ||
        (lastElement.metadata?.["promotional-message"] === true && secondLastElement?.subtype === "summary")
      );
    });
    return filteredRelatedStories;
  };

  const fetchRelatedStories = async () => {
    try {
      const response = await getRelatedStories(story.id, story.sections[0].id, SUMMARY_STORY_FIELDS, 15);
      setRelatedStories(response?.stories);
    } catch (error) {
      console.error("Failed to fetch related stories:", error);
    }
  };

  useEffect(() => {
    !collectionStories && fetchRelatedStories();
    return () => {
      setCurrentStory(0);
      window && window?.history?.replaceState(null, null, window.location.pathname);
    };
  }, [collectionStories]);

  useEffect(() => {
    if (pageType !== "summary-page" && relatedStories) {
      const filteredRelatedStories = relatedSummaryStoriesUtil(relatedStories);
      setAllStoriesLength(filteredRelatedStories.slice(0, 2).length + 1); // +1 for the main story
    }
  }, [relatedStories, pageType]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowSummary(false);
      setIsClosing(false);
    }, 300);
  };

  // Track summary card interactions for analytics
  const trackInteraction = (actionText) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtConfig,
        story
      );
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "short_news_button_click",
        link_text: actionText,
        category: pageType === "story-page" ? category : "short-news",
        sub_category: pageType === "story-page" ? subCategory : "NA",
        content_id: pageType === "story-page" ? contentId : "NA",
        template_name: pageType === "story-page" ? getTemplateName(story?.["story-template"] || pageType) : "listing",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const handleStoryChange = (direction) => {
    if (isTransitioning) return;
    // Track the button click
    trackInteraction(direction);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStory((prev) => {
        const newIndex = direction === "up" ? Math.max(0, prev - 1) : Math.min(summaryStories.length - 1, prev + 1);
        const newStory = summaryStories[newIndex];
        if (window && window?.location?.pathname === "/short-news") {
          if (newStory && newStory?.slug) {
            window && window?.history?.replaceState(null, null, `?slug=${newStory.slug}`);
          } else {
            window && window?.history?.replaceState(null, null, window.location.pathname);
          }
        }
        return newIndex;
      });
      setIsTransitioning(false);
      setIsExpanded(false);
    }, 100);
  };

  useEffect(() => {
    !summaryStories[currentStory]?.adSlot &&
      addDatalayerEventStory(summaryStories[currentStory], "shortstory", "short-news");
  }, [currentStory, collectionStories]);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (touchStartY.current === null) return;
    const currentY = e.touches[0].clientY;
    const deltaY = touchStartY.current - currentY;

    if (Math.abs(deltaY) > 50) {
      handleStoryChange(deltaY > 0 ? "down" : "up");
      touchStartY.current = null;
    }
  };

  const handleTouchEnd = () => {
    touchStartY.current = null;
  };

  const getCardStyle = (index) => {
    if (currentStory === index) return "active";
    if (currentStory === index - 1) return "next";
    if (currentStory === index + 1) return "prev";
    return "";
  };

  const summaryStories = [
    {
      image: story?.["hero-image-s3-key"],
      imageMetadata: story?.["hero-image-metadata"],
      imageCaption: story?.["hero-image-caption"],
      title: story?.headline,
      summary:
        last(last(story?.cards)?.["story-elements"])?.subtype === "summary"
          ? last(last(story?.cards)?.["story-elements"])?.text
          : nth(last(story?.cards)?.["story-elements"], -2)?.text,
      slug: story?.slug,
      url: story?.url,
      authors: story?.authors,
      "assignee-name": story?.["assignee-name"],
      "published-at": story?.["published-at"],
      "public-identifier": story?.["public-identifier"] || story?.["sequence-no"],
      "story-template": story?.["story-template"],
      adSlot: false,
    },
  ];
  if (relatedStories && pageType !== "summary-page") {
    const relatedSummaryStories = relatedSummaryStoriesUtil(relatedStories);
    const allStories = collectionStories ? relatedSummaryStories : relatedSummaryStories.slice(0, 2);
    allStories.forEach((relatedStory, index) => {
      summaryStories.push({
        image: relatedStory["hero-image-s3-key"],
        imageMetadata: relatedStory["hero-image-metadata"],
        imageCaption: relatedStory["hero-image-caption"],
        title: relatedStory.headline,
        summary:
          last(last(relatedStory.cards)["story-elements"]).subtype === "summary"
            ? last(last(relatedStory.cards)["story-elements"]).text
            : nth(last(relatedStory.cards)["story-elements"], -2).text,
        slug: relatedStory.slug,
        url: relatedStory.url,
        authors: relatedStory.authors,
        "assignee-name": relatedStory["assignee-name"],
        "published-at": relatedStory["published-at"],
        "public-identifier": relatedStory["public-identifier"] || relatedStory["sequence-no"],
        "story-template": relatedStory["story-template"],
        adSlot: false,
      });
      if (!isDesktop && index % 3 === 0 && !isStoryPage) {
        summaryStories.push({ adSlot: true });
      }
    });
  } else {
    collectionStories.forEach((story, index) => {
      summaryStories.push({
        image: story["hero-image-s3-key"],
        imageMetadata: story["hero-image-metadata"],
        imageCaption: story["hero-image-caption"],
        title: story.headline,
        summary:
          last(last(story.cards)["story-elements"]).subtype === "summary"
            ? last(last(story.cards)["story-elements"]).text
            : nth(last(story.cards)["story-elements"], -2).text,
        slug: story.slug,
        url: story.url,
        authors: story.authors,
        "assignee-name": story["assignee-name"],
        "published-at": story["published-at"],
        "public-identifier": story["public-identifier"] || story["sequence-no"],
        "story-template": story["story-template"],
        adSlot: false,
      });
      if (!isDesktop && index % 3 === 0 && !isStoryPage) {
        summaryStories.push({ adSlot: true });
      }
    });
  }

  // Auto-scroll to summary story via slug
  useEffect(() => {
    if (summaryStories.length === 0) return;
    const params = window && new URLSearchParams(window.location.search);
    const slugFromUrl = params?.get("slug");
    if (slugFromUrl) {
      const pathParts = slugFromUrl.split("/");
      const publicIdFromUrl = pathParts[pathParts.length - 1];
      if (publicIdFromUrl) {
        const index = summaryStories.findIndex((s) => s?.["public-identifier"] === publicIdFromUrl);
        if (index >= 0) {
          setCurrentStory(index);
        } else {
          setCurrentStory(0);
        }
      }
    }
  }, [summaryStories.length]);

  useEffect(() => {
    setTimeout(() => {
      const element = document.getElementById(`summary-${currentStory}`);
      const expand = document.getElementById(`expand-${currentStory}`);
      const shadow = document.getElementById(`shadow-${currentStory}`);
      if (element && expand) {
        if (element.scrollHeight <= element.clientHeight) {
          expand.style.pointerEvents = "none";
          expand.style.visibility = "hidden";
          if (shadow) {
            shadow.style.display = "none";
          }
        }
        if (element.scrollHeight > element.clientHeight) {
          expand.style.pointerEvents = "all";
          expand.style.visibility = "visible";
          if (shadow) {
            shadow.style.display = "block";
          }
        }
      }
    }, 200);
  }, [currentStory, collectionStories]);

  return (
    <div
      styleName={`summary-card-overlay ${isClosing ? "closing" : ""}`}
      className="summary-card-overlay"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div styleName="summary-card-container">
        <button onClick={() => handleClose()} styleName="close-button" className="summary-close">
          <span styleName="desktop-close">
            <SvgIconHandler type="close" width="18px" height="18px" />
          </span>
          <span styleName="mobile-close">
            <ArrowDashIcon height="20" width="20" viewBox="0 0 26 26" />
          </span>
        </button>
        <div styleName="story-container">
          {summaryStories.map((story, index) =>
            story.adSlot ? (
              <div
                key={index}
                className="story-summary"
                styleName={`story-card ${getCardStyle(index)} ad-slot-summary`}
              >
                <LazyAdSlot index={index} />
              </div>
            ) : (
              <div key={index} className="story-summary" styleName={`story-card ${getCardStyle(index)}`}>
                <div styleName={`story-image ${isExpanded ? "hiding" : ""}`}>
                  {story.image ? (
                    <ResponsiveImage
                      slug={story.image}
                      metadata={story.imageMetadata}
                      defaultWidth={440}
                      imgParams={{ auto: ["format", "compress"], fit: "fill", q: 70 }}
                      shouldDecodeImage={true}
                    />
                  ) : (
                    <div styleName="default-image"></div>
                  )}
                </div>
                <div styleName={`story-content ${isExpanded ? "story-content-expanded" : ""}`}>
                  <h2
                    styleName="story-title"
                    ref={(el) => {
                      if (el) {
                        const height = el.offsetHeight;
                        if (titleHeights[index] !== height) {
                          setTitleHeights((prev) => ({ ...prev, [index]: height }));
                        }
                        titleRefs.current[`ref${index}`] = el;
                      }
                    }}
                  >
                    {story.title}
                  </h2>
                  <p
                    id={`summary-${index}`}
                    onTouchStart={(e) => isExpanded && e.stopPropagation()}
                    onTouchMove={(e) => isExpanded && e.stopPropagation()}
                    onTouchEnd={(e) => isExpanded && e.stopPropagation()}
                    style={{
                      "--summary-sponsor": summary_sponsor ? "1" : "0",
                      "--title-height": `${titleHeights[index] || 0}px`,
                    }}
                    styleName={`story-summary ${
                      currentStory === summaryStories.length - 1 ? "story-summary-last" : ""
                    } ${
                      isExpanded
                        ? currentStory === summaryStories.length - 1
                          ? "story-summary-last-expanded"
                          : "story-summary-expanded"
                        : ""
                    }`}
                    dangerouslySetInnerHTML={{ __html: story.summary }}
                  />
                  <div id={`shadow-${index}`} styleName={isExpanded ? "" : "text-shadow"} />
                  <div id={`expand-${index}`} styleName="expand-actions">
                    <hr styleName="expand-line" />
                    <button
                      styleName="expand-button"
                      onClick={() => {
                        const newExpandState = !isExpanded;
                        // Track expand/collapse action
                        trackInteraction(newExpandState ? "expand" : "collapse");
                        setIsExpanded(newExpandState);
                        document.getElementById(`summary-${index}`).scrollTop = 0;
                      }}
                    >
                      {isExpanded ? "Collapse" : "Expand"}
                      <span styleName={`arrow-svg ${isExpanded ? "svg-flipped" : ""}`}>
                        <SvgIconHandler type="arrow" width="24px" height="24px" />
                      </span>
                    </button>
                  </div>
                  <div styleName="story-actions">
                    <div styleName="story-icons">
                      <div styleName="share-svg">
                        <SocialShare
                          template={SocialShareTemplate}
                          fullUrl={
                            window && window?.location?.pathname === "/short-news"
                              ? encodeURI(window?.location?.href)
                              : encodeURI(story.url)
                          }
                          title={story.title}
                          shareIconId={`share-icon-story-share-${story.id}`}
                          story={story}
                          isSummaryCard={true}
                        />
                      </div>
                      {isStoryPage && !isDesktop && (
                        <a href="/short-news">
                          <CategoryIcon />
                        </a>
                      )}
                    </div>
                    <a
                      styleName="full-story-button"
                      href={collectionStories ? `/${story.slug}` : index !== 0 ? `/${story.slug}` : "#"}
                      onClick={() => {
                        trackInteraction("full-story");
                        if (!collectionStories && index === 0) {
                          setShowSummary(false);
                        }
                      }}
                    >
                      {FULL_STORY}
                      <span styleName="arrow-rotated">
                        <SvgIconHandler type="arrow" width="24px" height="24px" />
                      </span>
                    </a>
                  </div>
                  {index + 1 === allStoriesLength && !collectionStories && (
                    <div styleName="view-all" onClick={() => trackInteraction("view all summaries")}>
                      <a href="/short-news">{VIEW_ALL_SUMMARIES}</a>
                    </div>
                  )}
                  <div styleName="ai-warning">
                    <ExclamatinoLogo width="12" height="12" viewBox="0 0 14 14" />
                    {AI_WARNING}
                  </div>
                </div>
                {summary_sponsor && (
                  <>
                    <hr styleName="bottom-line" />
                    <div styleName="sponsor-hdfc">
                      {POWERED_BY}
                      {" X"}
                    </div>
                  </>
                )}
              </div>
            )
          )}
        </div>
        <div styleName="navigation-controls">
          <button
            styleName={`nav-button ${currentStory === 0 ? "disabled" : ""}`}
            onClick={() => handleStoryChange("up")}
            disabled={currentStory === 0 || isTransitioning}
          >
            <span styleName="svg-flipped">
              <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
            </span>
          </button>
          <button
            styleName={`nav-button ${currentStory === summaryStories.length - 1 ? "disabled" : ""}`}
            onClick={() => handleStoryChange("down")}
            disabled={currentStory === summaryStories.length - 1 || isTransitioning}
          >
            <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
          </button>
        </div>
      </div>
    </div>
  );
};

SummaryCard.propTypes = {
  story: PropTypes.object,
  setShowSummary: PropTypes.func,
  summaryIndex: PropTypes.string,
  collectionStories: PropTypes.array,
};

export default SummaryCard;
